<script lang="ts">
	import Popup from '$components/Popup.svelte';
	import type { Placement } from '@floating-ui/dom';
	import type { SelectOption } from '$lib/shared/lib/popup-types';
	import { goto } from '$app/navigation';
	import Fa from 'svelte-fa';
	import { faChevronDown } from '@fortawesome/free-solid-svg-icons';
	import { generateId, resolveAny } from '$lib/shared/lib';

	interface Props {
		options?: SelectOption[];
		selected?: string;
		placeholder?: string;
		searchable?: boolean;
		buttonClass?: string;
		maxHeight?: string;
		popupId?: string;
		showAvatars?: boolean;
		placement?: Placement;
		onchange?: (detail: { value: string; option: SelectOption }) => void;
	}

	let {
		options = [],
		selected = $bindable(''),
		placeholder = 'Select option',
		searchable = true,
		buttonClass = 'btn w-full justify-between',
		maxHeight = 'max-h-64',
		popupId: _popupId = generateId('popup-select'),
		showAvatars = false,
		placement = 'bottom',
		onchange
	}: Props = $props();

	let searchTerm = $state('');
	let searchInput: HTMLInputElement = $state()!;
	let triggerEl: HTMLButtonElement = $state()!;
	let popupOpen = $state(false);

	function handleClickOutside(e: MouseEvent) {
		if (!triggerEl) return;
		const target = e.target as Node;
		if (triggerEl.contains(target)) return;
		// .dropdown-option items inside the popup trigger close as well
		if ((target as HTMLElement).closest?.('.dropdown-option')) {
			popupOpen = false;
			return;
		}
		popupOpen = false;
	}

	let filteredOptions = $derived(
		searchable
			? options.filter(
					(option) =>
						option.label.toLowerCase().includes(searchTerm.toLowerCase()) ||
						option.value.toLowerCase().includes(searchTerm.toLowerCase())
				)
			: options
	);

	let selectedOption = $derived(options.find((option) => option.value === selected));
	let displayText = $derived(selectedOption?.label || placeholder);

	function handleOptionSelect(option: SelectOption) {
		const old = selected;
		selected = option.value;
		searchTerm = '';
		// Notify parent components that the user selected an option interactively
		// Only invoke the callback when the selection actually changed
		if (old !== selected) {
			onchange?.({ value: option.value, option });
		}

		if (option.href) {
			goto(resolveAny(option.href));
		}
	}

	function handleTriggerClick() {
		popupOpen = !popupOpen;
		if (popupOpen && searchable) {
			setTimeout(() => searchInput?.focus(), 0);
		}
	}
</script>

<svelte:window onclick={handleClickOutside} />

<!-- Trigger Button -->
<button
	bind:this={triggerEl}
	class="{buttonClass} hover:cursor-pointer"
	type="button"
	onclick={handleTriggerClick}
>
	{#if showAvatars && selectedOption}
		{#if selectedOption.avatar}
			<img
				src={selectedOption.avatar}
				alt=""
				class="desktop-only h-8 w-8 rounded-full border-2 border-current object-cover"
			/>
		{:else}
			<div
				class="desktop-only inline-block h-8 w-8 rounded-full border-2 border-current bg-surface-200-800 text-center align-middle text-sm font-semibold"
				style="line-height: calc(2rem - 4px);"
			>
				{selectedOption.label.charAt(0).toUpperCase()}
			</div>
		{/if}
	{/if}
	<span class="truncate">{displayText}</span>
	<Fa icon={faChevronDown} class="ml-2" size="xs" />
</button>

<!-- Popup Content -->
<Popup bind:open={popupOpen} {triggerEl} {placement}>
	<div class="card rounded-lg border border-surface-200-800 bg-surface-100-900 !p-0 shadow-xl">
		{#if searchable}
			<div class="border-b border-surface-200-800 p-2">
				<input
					bind:this={searchInput}
					bind:value={searchTerm}
					type="search"
					placeholder="Search"
					class="input w-full rounded-full!"
				/>
			</div>
		{/if}

		<nav class="list-nav">
			<ul class="{maxHeight} overflow-y-auto">
				{#each filteredOptions as option (option.value)}
					<li
						class="flex cursor-pointer flex-row items-center gap-3 p-2 hover:bg-surface-200-800"
						class:preset-filled-surface-500={option.value === selected}
					>
						{#if showAvatars}
							{#if option.avatar}
								<img
									src={option.avatar}
									alt=""
									class="h-8 w-8 shrink-0 rounded-full border-2 border-current object-cover"
								/>
							{:else}
								<div
									class="inline-block h-8 w-8 shrink-0 rounded-full border-2 border-current bg-surface-200-800 text-center text-sm font-semibold"
									style="line-height: calc(2rem - 4px);"
								>
									{option.label.charAt(0).toUpperCase()}
								</div>
							{/if}
						{/if}
						<!-- These class:hover classes are overwriting default hover stuff thats conflicts -->
						<button
							class="dropdown-option w-full text-left text-surface-800-200"
							class:hover:!bg-surface-200-800={option.value !== selected}
							class:hover:!preset-filled-surface-500={option.value === selected}
							onclick={() => handleOptionSelect(option)}
						>
							<span class="truncate">{option.label}</span>
						</button>
					</li>
				{/each}
				{#if filteredOptions.length === 0}
					<li class="p-3 text-center text-surface-800-200 italic">No matches found</li>
				{/if}
			</ul>
		</nav>
	</div>
</Popup>
